import { NavigationGuard, NavigationGuardProps, closeModalById, useScopedSelector } from 'ia-react-core';
import { MfaOnboarding, MfaOnboardingResourceInfo, Constants as AccountConstants } from 'we-package-react-account';
import { PageContext } from 'omni-package-react-core';
import React, { FC, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Location, useLocation } from 'react-router-dom';
import Constants from '~/app/Constants';
import useIsOnboardingCompleted from '~/hooks/useIsOnboardingCompleted';
import { getCompanyByPath } from '~/utils/companyUtils';
import { Company } from '~/interfaces/CompanyConfig';

export interface CanQuitGuardProps extends NavigationGuardProps {}

export const CanQuitGuardName = 'CanQuitGuard';

const CanQuitGuard: FC<CanQuitGuardProps> = (props) => {
  const { t } = useTranslation('CanQuitGuard');
  const { path } = useContext(PageContext);
  const location = useLocation();
  const dispatch = useDispatch();

  const isMfaPage = useMemo(() => (
    location.pathname.startsWith(AccountConstants.MfaValidationPath)
  ), [location.pathname]);

  const isCentralizationPage = useMemo(() => (
    location.pathname.includes(Constants.PagesUrl.CentralizationBaseFragment)
  ), [location.pathname]);

  // MFA status flags
  const mfaOnboarding: MfaOnboarding = useScopedSelector(MfaOnboardingResourceInfo.dataDst);
  const mfaOnboardingStatus = (mfaOnboarding?.data as { onboardingStatus: string })?.onboardingStatus;
  const isMfaPostOnboardingFlow = isMfaPage && mfaOnboardingStatus === 'completed';

  // Centralization status flags
  const company = getCompanyByPath(location.pathname);
  const isExternalCompany = isCentralizationPage && company !== Company.DEFAULT;
  const isCentralizedAuthCompleted = useIsOnboardingCompleted();

  /**
   * Enforces correct logoff paths based on authentication context.
   *
   * Logoff Routes:
   * - SecureLogOff (/secure/logoff): Centralized accounts only
   * - LogOff (/log-off): Non-centralized accounts
   *   - DEFAULT (IA): Standard logout
   *   - EXTERNAL (IAAH, PW, INVESTIA): Passes company in state to OpenIdConnectorWrapper
   *     which handles the actual redirect to external domain (e.g., dev.clients.investia.ca)
   *
   * Special case: Post-onboarding MFA allows all navigation (user reconfiguring MFA)
   */
  const handleNavigate = useCallback((options: { data: { nextLocation: Location }; modalId: number }) => {
    const nextPath = options.data.nextLocation.pathname;

    // Allow all navigation for post-onboarding MFA flow (user is only reconfiguring MFA)
    if (isMfaPostOnboardingFlow) return true;

    // Allow navigation from centralization form to its confirmation page
    if (isCentralizationPage && nextPath.startsWith(Constants.PagesUrl.CentralizationBaseFragment) && nextPath.endsWith('/confirmation')) {
      return true;
    }

    // Determine allowed logoff path based on context
    const isMandatoryNonCentralizationFlow = (isMfaPage && !isMfaPostOnboardingFlow) || (!isMfaPage && !isCentralizationPage);
    const allowedPath = (isMandatoryNonCentralizationFlow && isCentralizedAuthCompleted)
      ? Constants.PagesUrl.SecureLogOff
      : Constants.PagesUrl.LogOff;

    // Allow navigation if already going to the correct logoff path
    if (nextPath === allowedPath) return true;

    // Intercept navigation and redirect to allowed path
    dispatch(closeModalById(options.modalId));

    const navigationData: { originalNext: string; company?: Company } = { originalNext: nextPath };
    if (isExternalCompany) {
      navigationData.company = company;
    }

    return [allowedPath, { data: navigationData }] as [string, Object];
  }, [dispatch, isMfaPostOnboardingFlow, isMfaPage, isCentralizationPage, isCentralizedAuthCompleted, isExternalCompany, company]);

  // Use broader path for centralization to allow form → confirmation navigation
  const guardPath = useMemo(() => (
    isCentralizationPage ? Constants.PagesUrl.CentralizationBaseFragment : path
  ), [isCentralizationPage, path]);

  return (
    <NavigationGuard
      path={guardPath}
      modalMessage={isMfaPostOnboardingFlow ? undefined : t('modalMessage')}
      reverseButtons={!isMfaPostOnboardingFlow}
      onNavigate={handleNavigate}
      {...props}
    />
  );
};
export default CanQuitGuard;
